import { getAppEnv } from "src/utils/env"

/**
 * 下タブの未読連絡数キャッシュサービス
 *
 * 用途:
 * - 下タブの「連絡網」アイコンに表示される未読数バッジをキャッシュ
 * - 連絡詳細を開いて既読にした際に、楽観的に未読数を減算
 * - API呼び出しを抑えつつ、バッジ表示を正しく保つ
 *
 * キャッシュ戦略:
 * - アプリ起動時: APIから取得してキャッシュ
 * - 連絡を既読にした時: キャッシュを-1して楽観的更新
 * - Pull-to-Refresh時: APIから再取得してキャッシュ更新
 * - アプリ終了時: メモリキャッシュはクリアされる（永続化しない）
 */

// インメモリキャッシュ（アプリ起動中のみ保持）
let cachedUnreadCount: Record<string, number> | null = null
let lastFetchTime: number | null = null

/**
 * キャッシュの有効期限（環境ごとに異なる）
 * - DEV/STG: 5分（テストしやすい）
 * - PROD: 72時間（受信トレイと同じTTL）
 */
const getCacheTTL = (): number => {
  const env = getAppEnv()
  if (env === "dev" || env === "stg") {
    return 5 * 60 * 1000 // 5分
  }
  return 72 * 60 * 60 * 1000 // 72時間（3日）
}

const CACHE_TTL = getCacheTTL()

export class UnreadContactCountCacheService {
  /**
   * キャッシュを取得
   */
  static get(): Record<string, number> | null {
    // TTLチェック
    if (lastFetchTime && Date.now() - lastFetchTime > CACHE_TTL) {
      console.log("[UnreadContactCountCache] Cache expired")
      this.clear()
      return null
    }

    return cachedUnreadCount
  }

  /**
   * キャッシュを設定
   */
  static set(unreadCount: Record<string, number>): void {
    cachedUnreadCount = unreadCount
    lastFetchTime = Date.now()
    console.log("[UnreadContactCountCache] Cache set:", unreadCount)
  }

  /**
   * 特定の団体の未読数を楽観的に減算
   *
   * @param organizationId - 団体ID
   * @param decrementBy - 減算する数（デフォルト: 1）
   */
  static decrementOptimistically(organizationId: string, decrementBy = 1): void {
    if (!cachedUnreadCount) {
      console.log("[UnreadContactCountCache] No cache to decrement")
      return
    }

    const currentCount = cachedUnreadCount[organizationId] || 0
    const newCount = Math.max(0, currentCount - decrementBy) // 0未満にならないように

    cachedUnreadCount = {
      ...cachedUnreadCount,
      [organizationId]: newCount,
    }

    console.log("[UnreadContactCountCache] Optimistically decremented:", {
      organizationId,
      before: currentCount,
      after: newCount,
    })
  }

  /**
   * 特定の団体の未読数を楽観的に加算
   * ゴミ箱から未読連絡を復元した場合などに使用
   *
   * @param organizationId - 団体ID
   * @param incrementBy - 加算する数（デフォルト: 1）
   */
  static incrementOptimistically(organizationId: string, incrementBy = 1): void {
    if (!cachedUnreadCount) {
      console.log("[UnreadContactCountCache] No cache to increment")
      return
    }

    const currentCount = cachedUnreadCount[organizationId] || 0
    const newCount = currentCount + incrementBy

    cachedUnreadCount = {
      ...cachedUnreadCount,
      [organizationId]: newCount,
    }

    console.log("[UnreadContactCountCache] Optimistically incremented:", {
      organizationId,
      before: currentCount,
      after: newCount,
    })
  }

  /**
   * キャッシュをクリア
   */
  static clear(): void {
    cachedUnreadCount = null
    lastFetchTime = null
    console.log("[UnreadContactCountCache] Cache cleared")
  }

  /**
   * キャッシュが存在するか確認
   */
  static has(): boolean {
    return cachedUnreadCount !== null
  }

  /**
   * キャッシュが有効か確認（TTLチェック）
   */
  static isValid(): boolean {
    if (!cachedUnreadCount || !lastFetchTime) {
      return false
    }

    return Date.now() - lastFetchTime <= CACHE_TTL
  }
}
